import {
  Security as PermissionsIcon,
  Person as PersonIcon,
  Pets as PetsIcon,
  AccessTime as TimeIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import React, { useCallback, useEffect, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'

import EmptyState from '../components/EmptyState'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'
import type { SharedPet, VetShare } from './types'

const SharedWithMe: React.FC = () => {
  const navigate = useNavigate()
  const translate = useTranslate()
  const notify = useNotify()
  const [shares, setShares] = useState<VetShare[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [declining, setDeclining] = useState<string | number | null>(null)

  const handleDecline = async (share: VetShare) => {
    if (!window.confirm(translate('pos.pet_shares.decline_confirm'))) return
    try {
      setDeclining(share.id)
      const response = await deadlineFetch(`/api/vet-shares/decline/${share.id}`, {
        method: 'POST',
        headers: authHeaders(),
      })
      const data = await response.json()
      if (response.ok && data?.success) {
        notify('pos.pet_shares.share_declined', { type: 'success' })
        fetchShares()
      } else {
        notify(data?.error || translate('pos.pet_shares.decline_failed'), { type: 'error' })
      }
    } catch (_e) {
      notify('pos.pet_shares.decline_failed', { type: 'error' })
    } finally {
      setDeclining(null)
    }
  }

  const fetchShares = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await deadlineFetch('/api/vet-shares/shared-with-me', {
        headers: authHeaders(),
      })

      if (!response.ok) {
        throw new Error('Failed to fetch shared data')
      }

      const result = await response.json()
      setShares(result.data || [])
      setError(null)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchShares()

    // Refresh every 30 seconds
    const interval = setInterval(fetchShares, 30000)
    return () => clearInterval(interval)
  }, [fetchShares])

  const getPermissionSummary = (permissions: string[]) => {
    return permissions
      .map((p) => translate(`pos.shared_with_me.permissions.${p}`, { _: p }))
      .join(', ')
  }

  const getShareTypeDisplay = (share: VetShare) => {
    if (share.shareType === 'all_pets') {
      return translate('pos.shared_with_me.all_pets')
    }
    const count = share.sharedPets?.length || 0
    return translate('pos.shared_with_me.specific_pets', { smart_count: count, count })
  }

  const handleViewPet = (petId: string) => {
    navigate(`/pets/${petId}/show`)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {translate('pos.shared_with_me.load_failed')}
      </Alert>
    )
  }

  if (shares.length === 0) {
    return (
      <EmptyState
        icon={<PersonIcon />}
        title={translate('pos.shared_with_me.empty_title')}
        description={translate('pos.shared_with_me.empty_subtitle')}
      />
    )
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Stack spacing={2}>
        {shares.map((share: VetShare) => (
          <Card key={share.id} variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="flex-start" mb={2}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                {/* Decline lives at the card level; button rendered after the flex body below */}
                <Box flex={1}>
                  <Typography variant="h6">
                    {translate('pos.shared_with_me.shared_by', { name: share.sharer?.name })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {share.sharer?.email}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => handleDecline(share)}
                    disabled={declining === share.id}
                    sx={{ mb: 1 }}
                  >
                    {translate('pos.pet_shares.decline')}
                  </Button>

                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip
                      size="small"
                      icon={<PetsIcon />}
                      label={getShareTypeDisplay(share)}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <PermissionsIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    {translate('pos.shared_with_me.you_can', {
                      permissions: getPermissionSummary(share.permissions),
                    })}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <TimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    {translate('pos.shared_with_me.shared_at', {
                      date: format(new Date(share.createdAt), 'MMM dd, yyyy HH:mm'),
                    })}
                  </Typography>

                  {share.expiresAt && (
                    <Typography variant="body2" color="text.secondary">
                      {translate('pos.shared_with_me.expires_at', {
                        date: format(new Date(share.expiresAt), 'MMM dd, yyyy'),
                      })}
                    </Typography>
                  )}

                  {share.note && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      "{share.note}"
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Show shared pets for specific_pets type */}
              {share.shareType === 'specific_pets' && share.sharedPets && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('pos.shared_with_me.shared_pets_label')}
                  </Typography>
                  <Stack spacing={1}>
                    {share.sharedPets.map((pet: SharedPet) => (
                      <Card key={pet.id} variant="outlined" sx={{ bgcolor: 'background.default' }}>
                        <CardContent sx={{ py: 1 }}>
                          <Box display="flex" justifyContent="between" alignItems="center">
                            <Box>
                              <Typography variant="subtitle2">{pet.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {translate('pos.shared_with_me.pet_meta', {
                                  species: pet.species,
                                  owner: pet.owner?.name ?? '',
                                })}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              startIcon={<ViewIcon />}
                              onClick={() => handleViewPet(String(pet.id))}
                            >
                              {translate('pos.shared_with_me.view_pet')}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* For all_pets, show general message */}
              {share.shareType === 'all_pets' && (
                <Box>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      {translate('pos.shared_with_me.all_pets_message', {
                        name: share.sharer?.name,
                      })}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  )
}

export default SharedWithMe
